<template>
    <div
        class="secondary-editorial-box"
        data-qa="secondary-editorial-box"
    >
        <div class="box-wrapper">
            <h4
                v-if="data.title"
                class="title"
            >
                {{ data.title }}
            </h4>

            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="data.text"
                class="text"
                v-html="localizeHrefs(data.text)"
            ></div>
            <!-- eslint-enable -->

            <ContentButton
                v-if="data.button"
                :data="data.button"
                page-type="product-listing"
            />
        </div>

        <div
            v-if="data.backgroundImage"
            class="box-background-image"
        >
            <CmsImage
                :data="data.backgroundImage"
                fit="cover"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { Banner1Fragment } from '@/graphql/api'

import ContentButton from '@/components/cms/component/content/ContentButton.vue'
import { computed } from 'vue'
import type { Colors } from '@/components/ui'
import { getColorForBackground } from '@/components/ui/buttons'
import { cmsColorToButtonColor } from '@/components/cms'
import CmsImage from '@/components/cms/CmsImage.vue'
import { useHrefLocalizer } from '@/router/href-localizer'

const props = defineProps<{
    data: Banner1Fragment
}>()

const localizeHrefs = useHrefLocalizer()

const backgroundColor = computed((): Colors => {
    return cmsColorToButtonColor(props.data.backgroundColor)
})

const textColor = computed((): Colors => {
    return getColorForBackground(
        cmsColorToButtonColor(props.data.backgroundColor)
    )
})
</script>

<style lang="scss" scoped>
.secondary-editorial-box {
    width: 100%;
    height: 100%;
    aspect-ratio: 3/4;
    background: v-bind(backgroundColor);
    color: v-bind(textColor);
    position: relative;
    grid-row: 8 / 8;
    display: flex;

    @include min-width(640) {
        aspect-ratio: initial;
        grid-column: 1 / span 2;
    }

    @include min-width(768) {
        grid-column: 1 / span 2;
        grid-row: 10 / 10;
    }

    @include min-width(1024) {
        grid-column: 1 / span 2;
        grid-row: 9 / 9;
    }

    @include hover() {
        .box-background-image {
            opacity: 1;
        }
    }

    .box-wrapper {
        width: 100%;
        height: 100%;
        position: relative;
        z-index: 3;
        padding: 40px 20px 20px;
        display: flex;
        align-items: center;
        align-content: center;
        justify-content: center;
        text-align: center;
        flex-wrap: wrap;

        .title {
            font-family: $parcel-font-stack;
            font-size: 1.5rem;
            line-height: 1.2em;
            width: 100%;
            text-align: center;
            margin: 0 0 20px;
        }

        .text {
            width: 100%;
            text-align: center;
        }
    }

    .box-background-image {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 2;
        opacity: 0;
        transition: opacity 0.5s ease-out;

        &::before {
            content: '';
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
            background: rgba($black, 0.3);
            display: block;
        }
    }
}
</style>
